/**
 * Hooks de estado (§2.4.2; FE §6.7). Componentes de `features/` leem os sinais por aqui: ler `.value` no render
 * inscreve o componente. useTweenDuration (§3.6.2, Fase 1) recebe o snapshot exibido por parâmetro.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { reducedMotion, useReducedMotion } from '../a11y/useReducedMotion';
import type { AppConfig } from '../config';
import type { IbgeCode } from '../contracts/base';
import type { MunicipalityInfo } from '../contracts/static';
import type { NightPhase } from '../model/nightPhase';
import type {
  FeedState,
  HeadlineState,
  LayoutState,
  MapMode,
  ResultsView,
  RouteOffice,
  RouteState,
  UfCode,
} from '../model/types';
import { chooseMyCity, forgetMyCity, loadMyCity } from './actions';
import { focusUf, hasFocus, headline, isAbroad, mapMode, nowMinute, offices, phase, tvOn, viewKey } from './derived';
import {
  config,
  feed,
  isSimulated,
  layout,
  myCity,
  type NowState,
  now,
  route,
  type StaticsState,
  statics,
  type TimelineState,
  timeline,
  type UiState,
  ui,
  view,
} from './store';

// ── Leitura dos sinais (§2.4.2 regra 1) ──────────────────────────────────────────────────────────────────────
/** Vista exibida (ao vivo ou replay); null antes do 1º snapshot. */
export function useView(): ResultsView | null {
  return view.value;
}
/** RouteState (§5.1). Nome de §2.4.2; não existe hook em router/ (FE §6.7, G-28). */
export function useRouteState(): RouteState {
  return route.value;
}
/** Alias de useRouteState (nome usado no prompt da Fase 2a e em §2.3.5). */
export const useRoute = useRouteState;
export function useLayout(): LayoutState {
  return layout.value;
}
export function useFeed(): FeedState {
  return feed.value;
}

/**
 * M1 da revisão cruzada (ORD-F-148): o aviso de falha com o link do TSE, quando o feed não entregou nenhum número desde
 * a abertura. Texto aprovado pelo responsável (§8 de 20/10; ORD-F-150): a trava está ligada. Objeto e não constante, para
 * os testes ligarem e desligarem.
 */
export const feedFailingNotice = { on: true };
/** Depois de quantas falhas seguidas, ou de quanto tempo sem nenhum número, o aviso de falha entra (M1). */
export const FEED_FAILING_AFTER_FAILURES = 3;
export const FEED_FAILING_AFTER_MS = 60_000;

/** Ao vivo, sem nenhum número na tela e com 3 falhas seguidas ou 60 s sem dado (M1; só com a trava ligada). */
export function useFeedFailing(): boolean {
  const f = feed.value;
  const noData = timeline.value.live && view.value === null;
  const [late, setLate] = useState(false);
  useEffect(() => {
    if (!noData) {
      setLate(false);
      return;
    }
    const t = setTimeout(() => setLate(true), FEED_FAILING_AFTER_MS);
    return () => clearTimeout(t);
  }, [noData]);
  return feedFailingNotice.on && noData && (f.failures >= FEED_FAILING_AFTER_FAILURES || late);
}
export function useUi(): UiState {
  return ui.value;
}
export function useTimeline(): TimelineState {
  return timeline.value;
}
/** Relógio da fonte `{ ms, tMin }`; não cria temporizador (AC-10.1-04): o tique é de state/effects.ts. */
export function useNow(): NowState {
  return now.value;
}
/** O minuto do relógio da fonte: redesenha só na virada do minuto (o fechamento das urnas do Exterior). */
export function useNowMinute(): number {
  return nowMinute.value;
}
/** Estáticos (DE-B01). */
export function useStatics(): StaticsState {
  return statics.value;
}
/** Configuração resolvida (congelada). */
export function useConfig(): AppConfig {
  return config.value;
}
/** Estado "Simulação" do App (§2.2, §2.4.1). */
export function useIsSimulated(): boolean {
  return isSimulated.value;
}

// ── Seletores de rota para a UI (state/derived.ts) ───────────────────────────────────────────────────────────
export function useViewKey(): string {
  return viewKey.value;
}
export function useFocusUf(): UfCode | null {
  return focusUf.value;
}
export function useIsAbroad(): boolean {
  return isAbroad.value;
}
/** Abas disponíveis (§5.4.1). */
export function useOffices(): RouteOffice[] {
  return offices.value;
}
/** Modo efetivo do mapa (§5.1). */
export function useMapMode(): MapMode {
  return mapMode.value;
}
/** `hasFocus` de §4.4.1. */
export function useHasFocus(): boolean {
  return hasFocus.value;
}
export function useTvOn(): boolean {
  return tvOn.value;
}

// ── Fase da noite e manchete (§13.1.2, §13.2; FE §9.2, G-10) ─────────────────────────────────────────────────
/** Fase da noite (§13.1.2): muda só quando a fase muda, então não renderiza a cada tique de `now`. */
export function usePhase(): NightPhase {
  return phase.value;
}
/** Estado da manchete exibido (`view.headline`, com contagem e abertura pelo relógio, H8). */
export function useHeadline(): HeadlineState {
  return headline.value;
}

// ── useTweenDuration (§3.6.2) ────────────────────────────────────────────────────────────────────────────────

export const TWEEN_FIRST_MS = 6000;
export const TWEEN_MIN_MS = 1500;
export const TWEEN_MAX_MS = 20000;
/** Sem interpolação contínua: replay, movimento reduzido, aba oculta ou salto > 10 min de t_min (§3.6.2). */
export const TWEEN_SNAP_MS = 300;
const JUMP_T_MIN = 10;

export interface TweenState {
  ema: number;
  lastSeq: number | null;
  lastAt: number | null;
  lastTMin: number | null;
  tween: number;
}

export interface TweenInput {
  /** seq do snapshot exibido (null antes do primeiro). */
  seq: number | null;
  /** t_min do snapshot exibido. */
  tMin: number | null;
  /** timeline.live; false = replay. */
  live: boolean;
  /** Instante da chegada (ms, relógio monotônico). */
  now: number;
  reduced: boolean;
  hidden: boolean;
}

export const initialTween = (): TweenState => ({
  ema: TWEEN_FIRST_MS,
  lastSeq: null,
  lastAt: null,
  lastTMin: null,
  tween: TWEEN_FIRST_MS * 0.85,
});

/**
 * Passo puro de §3.6.2: a cada seq NOVO (maior), ema = 0,7·ema + 0,3·intervalo; tween = clamp(ema × 0,85, 1,5 s, 20 s).
 * Vale 300 ms no replay, com movimento reduzido, com a aba oculta ou num salto de mais de 10 min de t_min.
 */
export function nextTween(prev: TweenState, input: TweenInput): TweenState {
  let { ema, lastSeq, lastAt, lastTMin } = prev;
  let jumped = false;
  if (input.seq !== null && (lastSeq === null || input.seq > lastSeq)) {
    if (lastAt !== null) ema = 0.7 * ema + 0.3 * (input.now - lastAt);
    if (lastTMin !== null && input.tMin !== null && Math.abs(input.tMin - lastTMin) > JUMP_T_MIN) jumped = true;
    lastSeq = input.seq;
    lastAt = input.now;
    lastTMin = input.tMin;
  }
  const snap = !input.live || input.reduced || input.hidden || jumped;
  const tween = snap ? TWEEN_SNAP_MS : Math.min(TWEEN_MAX_MS, Math.max(TWEEN_MIN_MS, ema * 0.85));
  return { ema, lastSeq, lastAt, lastTMin, tween };
}

/** Escreve --dur-live-tween no :root (CSSOM, compatível com a CSP). */
export function writeTween(ms: number): void {
  document.documentElement.style.setProperty('--dur-live-tween', `${Math.round(ms)}ms`);
}

/**
 * Calcula a duração do tween e grava --dur-live-tween. Chamado uma vez, no App. AnimatedNumber lê a variável
 * (DE-13) e salta direto quando ela vale 300 ms.
 */
export function useTweenDuration(seq: number | null, tMin: number | null, live: boolean): number {
  const reduced = useReducedMotion();
  const state = useRef<TweenState>(initialTween());
  const hidden = typeof document !== 'undefined' && document.hidden;
  state.current = nextTween(state.current, { seq, tMin, live, now: performance.now(), reduced, hidden });
  const ms = state.current.tween;
  useEffect(() => {
    writeTween(ms);
  }, [ms]);
  useEffect(() => {
    const onVisibility = () => writeTween(document.hidden || reducedMotion.value ? TWEEN_SNAP_MS : state.current.tween);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  return ms;
}

/** D3: o município guardado no aparelho; null sem `features.my_city` ou sem escolha. Lê o aparelho uma vez. */
export function useMyCity(): IbgeCode | null {
  const on = config.value.features.my_city;
  useEffect(() => {
    if (on) loadMyCity();
  }, [on]);
  return on ? myCity.value : null;
}

/** D3: o que o campo "Escolha o seu município" (components/MyCityPicker) recebe da página. */
export interface MyCityPickerState {
  cities: readonly MunicipalityInfo[];
  chosen: IbgeCode | null;
  onChoose(ibge: IbgeCode): void;
  onForget(): void;
}

/**
 * D3 (§72): a chave, o município guardado, o cadastro e as ações do campo, numa leitura só para a página inicial e as
 * duas pré-apurações. null sem `features.my_city` (aí não lê o aparelho) ou sem a geografia: o campo não existe.
 */
export function useMyCityPicker(): MyCityPickerState | null {
  const on = config.value.features.my_city;
  const chosen = useMyCity();
  const geo = statics.value.geography;
  if (!on || !geo) return null;
  return { cities: geo.municipalities, chosen, onChoose: chooseMyCity, onForget: forgetMyCity };
}
